import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, CheckCircle2, Quote } from "lucide-react";
import { SOLUTIONS_DETAIL } from "@/lib/solutions";
import PACSimulator from "./PACSimulator";
import TimelapseTeaser from "./TimelapseTeaser";
import SchematicBeforeAfter from "./SchematicBeforeAfter";

function BeforeAfter({ before, after, label = "Avant / Après" }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(50);
  const [dragging, setDragging] = useState(false);

  const update = (clientX) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const p = ((clientX - r.left) / r.width) * 100;
    setPos(Math.max(2, Math.min(98, p)));
  };

  return (
    <div
      ref={ref}
      className="relative aspect-[16/9] w-full overflow-hidden rounded-xl bg-[#0F172A] select-none touch-none"
      onMouseMove={(e) => dragging && update(e.clientX)}
      onMouseUp={() => setDragging(false)}
      onMouseLeave={() => setDragging(false)}
      onTouchMove={(e) => update(e.touches[0].clientX)}
      data-testid="before-after"
    >
      <img
        src={after}
        alt="Après"
        className="absolute inset-0 w-full h-full object-cover"
        draggable={false}
      />
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ width: `${pos}%` }}
      >
        <img
          src={before}
          alt="Avant"
          className="absolute inset-0 h-full object-cover"
          style={{ width: `${100 / (pos / 100)}%`, maxWidth: "none" }}
          draggable={false}
        />
      </div>

      {/* Labels */}
      <div className="absolute top-4 left-4 font-mono-tech text-[10px] uppercase tracking-[0.22em] text-[#fdfbf7] bg-[#0F172A]/70 backdrop-blur-sm px-2.5 py-1 rounded-full">
        Avant
      </div>
      <div className="absolute top-4 right-4 font-mono-tech text-[10px] uppercase tracking-[0.22em] text-[#0F172A] bg-[#FF9800] px-2.5 py-1 rounded-full">
        Après
      </div>

      {/* Divider */}
      <div
        className="absolute top-0 bottom-0 w-[3px] bg-[#FDFBF7] cursor-ew-resize"
        style={{ left: `${pos}%`, transform: "translateX(-50%)" }}
        onMouseDown={() => setDragging(true)}
        onTouchStart={() => setDragging(true)}
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-[#FDFBF7] shadow-xl flex items-center justify-center">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M7 5 L3 10 L7 15" stroke="#0F172A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M13 5 L17 10 L13 15" stroke="#0F172A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </div>

      {/* Hint */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 font-mono-tech text-[10px] uppercase tracking-[0.22em] text-[#FDFBF7]/80 pointer-events-none">
        Faites glisser →
      </div>
    </div>
  );
}

export default function SolutionDetail({ solutionId, onClose }) {
  const scrollRef = useRef(null);
  const d = solutionId ? SOLUTIONS_DETAIL[solutionId] : null;

  useEffect(() => {
    if (d) {
      document.body.classList.add("no-scroll");
      if (scrollRef.current) scrollRef.current.scrollTop = 0;
    }
    return () => document.body.classList.remove("no-scroll");
  }, [d]);

  const goContact = () => {
    onClose();
    setTimeout(() => {
      document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
    }, 250);
  };

  return (
    <AnimatePresence>
      {d && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-[#0F172A]/70 backdrop-blur-sm"
          onClick={onClose}
          data-testid={`solution-detail-${d.id}`}
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            ref={scrollRef}
            className="absolute inset-x-0 top-6 lg:top-10 bottom-0 mx-auto max-w-6xl bg-[#FDFBF7] rounded-t-3xl overflow-y-auto"
          >
            {/* Sticky header */}
            <div className="sticky top-0 z-10 bg-[#FDFBF7]/95 backdrop-blur-sm border-b border-[#e2d9ce] px-6 lg:px-10 py-4 flex items-center justify-between">
              <div className="font-mono-tech text-[10px] uppercase tracking-[0.22em] text-[#64748B]">
                Solution HMCD · Détail
              </div>
              <button
                onClick={onClose}
                className="h-10 w-10 rounded-full bg-[#F5F1EA] hover:bg-[#0F172A] hover:text-[#FDFBF7] transition-colors flex items-center justify-center"
                data-testid="solution-detail-close"
                aria-label="Fermer"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Hero image */}
            <div className="relative aspect-[16/8] w-full overflow-hidden">
              <img src={d.hero} alt={d.title} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/70 via-[#0F172A]/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-10 text-[#FDFBF7]">
                <div className="eyebrow !text-[#FF9800] mb-3">HMCD · Fiche solution</div>
                <h2 className="font-serif-display text-3xl sm:text-5xl lg:text-6xl leading-[1] tracking-tight max-w-3xl balance">
                  {d.title}
                </h2>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 lg:px-10 py-10 lg:py-14">
              <p className="font-serif-display text-xl lg:text-2xl leading-snug text-[#0F172A] max-w-3xl balance">
                {d.subtitle}
              </p>
              <p className="mt-6 text-[15px] lg:text-[16px] leading-relaxed text-[#334155] max-w-3xl">
                {d.intro}
              </p>

              {/* Stats */}
              {d.stats?.length > 0 && (
                <div className="mt-10 lg:mt-14 grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
                  {d.stats.map((s, i) => (
                    <div
                      key={i}
                      className="border-l-2 border-[#E65100] pl-4 py-2"
                      data-testid={`solution-stat-${d.id}-${i}`}
                    >
                      <div className="font-serif-display text-3xl lg:text-4xl text-[#0F172A]">
                        {s.value}
                      </div>
                      <div className="font-mono-tech text-[10px] uppercase tracking-[0.22em] text-[#64748B] mt-2">
                        {s.label}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Why */}
              {d.why?.length > 0 && (
                <section className="mt-14 lg:mt-20">
                  <div className="eyebrow mb-4">Pourquoi le faire ?</div>
                  <h3 className="font-serif-display text-3xl lg:text-5xl leading-[1] tracking-tight text-[#0F172A] max-w-2xl balance">
                    Les bénéfices, en clair.
                  </h3>
                  <div className="mt-8 lg:mt-10 grid md:grid-cols-2 gap-4 lg:gap-5">
                    {d.why.map((w, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-40px" }}
                        transition={{ duration: 0.5, delay: (i % 3) * 0.06 }}
                        className="flex items-start gap-4 p-5 lg:p-6 bg-[#F5F1EA] border border-[#e2d9ce] rounded-xl"
                      >
                        <div className="h-9 w-9 rounded-full bg-[#E6F4EA] text-[#2D7A4D] flex items-center justify-center shrink-0">
                          <CheckCircle2 className="h-4 w-4" />
                        </div>
                        <div>
                          <div className="font-serif-display text-lg lg:text-xl text-[#0F172A] leading-tight">
                            {w.title}
                          </div>
                          <div className="mt-1.5 text-sm text-[#334155] leading-relaxed">
                            {w.body}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </section>
              )}

              {/* Process */}
              {d.process?.length > 0 && (
                <section className="mt-14 lg:mt-20">
                  <div className="eyebrow mb-4">Notre méthode</div>
                  <h3 className="font-serif-display text-3xl lg:text-5xl leading-[1] tracking-tight text-[#0F172A] max-w-2xl balance">
                    Comment on procède.
                  </h3>
                  <div className="mt-8 lg:mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
                    {d.process.map((p, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-40px" }}
                        transition={{ duration: 0.5, delay: i * 0.08 }}
                        className="relative p-5 lg:p-6 border border-[#e2d9ce] rounded-xl bg-[#FDFBF7]"
                      >
                        <div className="font-serif-display text-4xl lg:text-5xl text-[#E65100] leading-none mb-4">
                          {p.step}
                        </div>
                        <div className="font-serif-display text-lg text-[#0F172A] leading-tight">
                          {p.title}
                        </div>
                        <div className="mt-2 text-sm text-[#334155] leading-relaxed">
                          {p.body}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </section>
              )}

              {/* Timelapse video teaser — for main solutions */}
              {["solar", "ite", "hvac", "battery", "water"].includes(d.id) && (
                <TimelapseTeaser solutionId={d.id} />
              )}

              {/* PAC simulator — only for hvac */}
              {d.id === "hvac" && <PACSimulator />}

              {/* Before / After Projects */}
              {d.projects?.length > 0 && (
                <section className="mt-14 lg:mt-20">
                  <div className="eyebrow mb-4">Nos réalisations · Avant / après</div>
                  <h3 className="font-serif-display text-3xl lg:text-5xl leading-[1] tracking-tight text-[#0F172A] max-w-3xl balance">
                    La transformation, en un glissement.
                  </h3>
                  <div className="mt-8 lg:mt-10 space-y-10 lg:space-y-14">
                    {d.projects.map((p, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.7 }}
                        data-testid={`solution-project-${d.id}-${i}`}
                      >
                        {p.collage ? (
                          <div className="relative rounded-xl overflow-hidden border border-[#e2d9ce] bg-[#0F172A]">
                            <img
                              src={p.collage}
                              alt={p.title}
                              className="w-full h-auto block"
                            />
                            <div className="absolute top-4 right-4 font-mono-tech text-[10px] uppercase tracking-[0.22em] text-[#0F172A] bg-[#FF9800] px-2.5 py-1 rounded-full shadow-lg">
                              Chantier HMCD · Réel
                            </div>
                          </div>
                        ) : p.schema ? (
                          <SchematicBeforeAfter type={p.schema} />
                        ) : (
                          <BeforeAfter before={p.before} after={p.after} />
                        )}
                        <div className="mt-5 grid lg:grid-cols-3 gap-4 lg:gap-6 items-baseline">
                          <div className="lg:col-span-1">
                            <div className="font-mono-tech text-[10px] uppercase tracking-[0.22em] text-[#64748B] mb-2">
                              Chantier N°{String(i + 1).padStart(2, "0")}
                            </div>
                            <div className="font-serif-display text-xl lg:text-2xl text-[#0F172A]">
                              {p.title}
                            </div>
                          </div>
                          <div className="lg:col-span-1">
                            <div className="font-mono-tech text-[10px] uppercase tracking-[0.22em] text-[#64748B] mb-2">
                              Équipement
                            </div>
                            <div className="text-[15px] text-[#0F172A]">{p.power}</div>
                          </div>
                          <div className="lg:col-span-1">
                            <div className="font-mono-tech text-[10px] uppercase tracking-[0.22em] text-[#64748B] mb-2">
                              Résultat
                            </div>
                            <div className="text-[15px] text-[#2D7A4D] font-medium">
                              {p.result}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </section>
              )}

              {/* Testimonial */}
              {d.testimonial && (
                <section className="mt-14 lg:mt-20 bg-[#0F172A] text-[#FDFBF7] rounded-2xl p-8 lg:p-12 relative overflow-hidden">
                  <Quote className="absolute top-8 right-8 h-16 w-16 text-[#FF9800]/40" />
                  <div className="eyebrow !text-[#FF9800] mb-4">Retour client</div>
                  <p className="font-serif-display text-2xl lg:text-4xl leading-[1.15] italic max-w-3xl">
                    « {d.testimonial.quote} »
                  </p>
                  <div className="mt-8 pt-6 border-t border-[#FDFBF7]/15">
                    <div className="font-medium">{d.testimonial.author}</div>
                    <div className="text-sm text-[#FDFBF7]/60 mt-1">{d.testimonial.role}</div>
                  </div>
                </section>
              )}

              {/* CTA */}
              <section className="mt-14 lg:mt-20 border-t border-[#e2d9ce] pt-10 lg:pt-14 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
                <div>
                  <h4 className="font-serif-display text-2xl lg:text-3xl text-[#0F172A] leading-tight max-w-lg balance">
                    Envie d'un devis chiffré ?
                  </h4>
                  <p className="mt-2 text-[15px] text-[#334155]">
                    Étude gratuite · Chiffrage sous 48 h · Sans engagement.
                  </p>
                </div>
                <button
                  data-testid={`solution-cta-${d.id}`}
                  onClick={goContact}
                  className="btn-accent"
                >
                  Demander un devis
                  <ArrowRight className="h-4 w-4" />
                </button>
              </section>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
